import { API } from 'components/CommonFunc/amplifyApiCompat';
import { ConsoleLogger } from 'aws-amplify/utils';
import {FormikHelpers, FormikValues} from "formik";
import React from "react";
import {Alert, Card, Col, Form, Row} from "react-bootstrap";
import {v4 as uuidv4} from "uuid";
import * as yup from "yup";
import {DataAudit} from "../../../CommonApi/DataAudit";
import {EventType, FrontendModule} from "../../../CommonApi/Enum";
import {UsageTracking} from "../../../CommonApi/UsageTracking";
import {ApiCallStatus} from "../../../CommonFunc/Enum";
import {DataImportFormSubmissionStatus, HtmlForm} from "../../Common/HtmlForm";
import {CollectionRound, View} from "../Enum";
import {genGenPlateApiConst} from "../GenGenPlateApiConst";
import {PlateRow} from "../PlateRow";
import {GenGenPlateCalculatedResult} from "./GenGenPlateCalculatedResult";
import {TanStackRawDataTable} from "./TanStackRawDataTable";

const logger = new ConsoleLogger("GenGenPlateRawDataForm")

export interface GenGenPlateRawData {
    plateId: string
    collectionRound: CollectionRound
    collectionDay: number
    collectionRawData: string
    owner?: string
    lastModified?: Date
}

interface GenGenPlateRawDataFormState {
    initialValues: GenGenPlateRawData
    submitStatus: DataImportFormSubmissionStatus
    getStatus: ApiCallStatus
    getMessage: string
    calculationResultRefreshToken: string
}

interface GenGenPlateRawDataFormProps {
    view: View
    plateId: string
    displayHeader: boolean
    initialValues: GenGenPlateRawData
}

export class GenGenPlateRawDataForm extends React.Component<GenGenPlateRawDataFormProps, GenGenPlateRawDataFormState> {
    static defaultProps = {
        plateId: "",
        displayHeader: true,
        initialValues: null,
    }

    constructor(props: GenGenPlateRawDataFormProps) {
        super(props);
        this.state = {
            initialValues: props.initialValues == null ? {
                plateId: "",
                collectionRound: CollectionRound.None,
                collectionDay: 0,
                collectionRawData: "",
            } : this.props.initialValues,
            submitStatus: {
                status: ApiCallStatus.NoData,
                message: ""
            },
            getStatus: ApiCallStatus.NoData,
            getMessage: "",
            calculationResultRefreshToken: uuidv4(),
        }
    }

    onSubmit(values: any, formikHelpers: FormikHelpers<FormikValues>): void | Promise<any> {
        formikHelpers.setSubmitting(true)
        this.setState({
            submitStatus: {
                status: ApiCallStatus.Loading,
                message: "",
            },
        }, () => {
            if (this.props.view === View.Import) {
                this.apiPut(values, formikHelpers)
            } else if (this.props.view === View.Update) {
                this.apiPatch(values, formikHelpers)
            }
        })
    }

    // Common API caller utility
    async apiCall(
        values: GenGenPlateRawData,
        formikHelpers: FormikHelpers<FormikValues>,
        options: {
            method: 'put' | 'patch',
            eventType: EventType,
            successMessageFn: (values: GenGenPlateRawData) => string,
            shouldResetForm: boolean,
            shouldRefreshToken: boolean,
            updateNote?: string,
        }
    ) {
        const { method, eventType, successMessageFn, shouldResetForm, shouldRefreshToken } = options
        logger.info(
            `GenGenPlateRawDataForm - api${method.charAt(0).toUpperCase() + method.slice(1)} - values = `,
            values
        )

        UsageTracking.recordEvent({
            uuid: uuidv4(),
            module: FrontendModule.DataCatalog,
            event: eventType,
            table: genGenPlateApiConst.tableName.RAW_DATA,
            database: genGenPlateApiConst.DATABASE_NAME,
            keywords: "",
            timestamp: new Date()
        })

        this.setState({
            submitStatus: {
                status: ApiCallStatus.Loading,
                message: "",
            }
        })

        try {
            const response = await API[method](
                genGenPlateApiConst.API_NAME,
                genGenPlateApiConst.path.RAW_DATA,
                {
                    body: values
                })

            DataAudit.recordEvent({
                database: genGenPlateApiConst.DATABASE_NAME,
                table: genGenPlateApiConst.tableName.RAW_DATA,
                rowId: values.plateId,
                updateNote: options.updateNote || "",
                dataObject: values
            })
            logger.info("Response from API ", response)

            const successMsg =successMessageFn(values)

            this.setState(prevState => {
                const newState = {
                    ...prevState,
                    initialValues: values,
                    submitStatus: {
                        status: ApiCallStatus.Completed,
                        message: successMsg
                    }
                }

                if (shouldRefreshToken) {
                    newState.calculationResultRefreshToken = uuidv4()
                }
                return newState
            }, () => {
                if (shouldResetForm) {
                    formikHelpers.resetForm()
                }
            })
        } catch (error: unknown) {
            logger.error(error)
            let errorMessage = "An unknown error occurred."
            if (error && typeof error === "object" && 'response' in error) {
                const apiError = error as { response?: { data?: { message?: string } } }
                if (apiError.response?.data?.message) {
                    errorMessage = apiError.response.data.message
                }
            } else if (error instanceof Error) {
                errorMessage = error.message
            } else if (typeof error === 'string') {
                errorMessage = error
            }

            this.setState({
                submitStatus: {
                    status: ApiCallStatus.Error,
                    message: errorMessage,
                }
            })
        } finally {
            formikHelpers.setSubmitting(false) // Don't reset form, ppl may want to compare raw data with the graph
        }
    }

    apiPut(values: GenGenPlateRawData, formikHelpers: FormikHelpers<FormikValues>) {
        // TODO in future - Raw data form is a special case, where it doesn't have owner, instead links to metadata table with owner column,
        // and it's important that people don't upload raw data of plate belongs to different owner
        // So we need to send current user name to back-end, to perform join with metadata table to check if owner === user

        const isFinalCollection = values.collectionRound === CollectionRound.Final
        this.apiCall(values, formikHelpers, {
            method: 'put',
            eventType: EventType.Import,
            successMessageFn: (values) => `Plate ${values.plateId}, ${values.collectionRound.toLowerCase()} collection round is uploaded`,
            shouldResetForm: !isFinalCollection,
            shouldRefreshToken: false,
        })
    }

    apiPatch(values: any, formikHelpers: FormikHelpers<FormikValues>) {
        const isFinalCollection = values.collectionRound.toLowerCase() === CollectionRound.Final
        this.apiCall(values, formikHelpers, {
            method: 'patch',
            eventType: EventType.Update,
            successMessageFn: (values) => `Plate ${values.plateId} updated`,
            shouldResetForm: false,
            shouldRefreshToken: isFinalCollection,
            updateNote: values.updateNote || "",
        })
    }

    render() {
        if (this.props.view === View.Read) {
            if (!this.state.initialValues || !this.state.initialValues.collectionRawData || this.state.initialValues.collectionRawData === "") {
                return <Alert variant={"info"}>No raw data uploaded</Alert>
            }

            return <Row>
                <Col md={12}>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            {this.state.initialValues.collectionRound.charAt(0).toUpperCase() + this.state.initialValues.collectionRound.slice(1)} collection round - day {this.state.initialValues.collectionDay}
                        </Form.Label>
                        <TanStackRawDataTable
                            data={PlateRow.convertStrToPlateMapRowList(this.state.initialValues.collectionRawData)}
                        />
                    </Form.Group>
                </Col>
                <Col md={12}>
                    <GenGenPlateCalculatedResult
                        collectionRound={this.state.initialValues.collectionRound}
                        refreshToken={this.state.calculationResultRefreshToken}
                        view={this.props.view}
                        plateId={this.state.initialValues.plateId}/>
                </Col>
            </Row>
        }

        return <Card bg={"light"}>
            {this.props.displayHeader && <Card.Header><b>Add new gen-gen plate raw data</b></Card.Header>}
            <Card.Body>
                <HtmlForm
                    databaseName={genGenPlateApiConst.DATABASE_NAME}
                    tableName={genGenPlateApiConst.tableName.RAW_DATA}
                    initialValues={this.state.initialValues}
                    rowsOfFormFields={[
                        {
                            colSize: 4,
                            fields: [
                                {name: "plateId", dataType: "text", disable: this.props.view === View.Update},
                                {name: "collectionRound", dataType: "select", disable: false},
                                {name: "collectionDay", dataType: "number", disable: false},
                            ]
                        },
                        {
                            colSize: 12,
                            fields: [
                                {name: "collectionRawData", dataType: "textarea", disable: false}
                            ]
                        },
                    ]}
                    schema={yup.object({
                        plateId: yup.string().required(),
                        collectionRound: yup.string().required(),
                        collectionDay: yup.number().moreThan(0).required(),
                        collectionRawData: yup.string().matches(/^[0-9.\s]+$/, "Must contains only numbers and white spaces").required(),
                        additionalNote: yup.string(),
                    })}
                    onSubmit={this.onSubmit.bind(this)}
                    submissionStatus={this.state.submitStatus}
                    autofillOwnerByLogInId={false}
                    view={this.props.view}
                />
                {
                    this.state.initialValues.plateId !== "" &&
                    this.state.submitStatus.status === ApiCallStatus.Completed &&
                    <GenGenPlateCalculatedResult
                        collectionRound={this.state.initialValues.collectionRound}
                        refreshToken={this.state.calculationResultRefreshToken}
                        view={this.props.view}
                        plateId={this.state.initialValues.plateId}/>
                }
            </Card.Body>
        </Card>;
    }

}